// src/components/EmailAuthForm.js — Discord/E-Mail Login & Registrierung (Tabs auf einer Karte)
import React, { useState } from 'react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const s = {
  tabs: { display: 'flex', gap: '0.4rem', marginBottom: '1.25rem', background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '0.3rem' },
  tab: { flex: 1, padding: '0.6rem', textAlign: 'center', borderRadius: '8px', border: 'none', background: 'transparent', color: 'var(--muted)', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'var(--font-head)', letterSpacing: '0.02em', transition: 'all 0.2s' },
  tabActive: { background: 'var(--cyan)', color: '#0a0a0a' },
  subTabs: { display: 'flex', gap: '1.25rem', marginBottom: '1.25rem', borderBottom: '1px solid var(--border)' },
  subTab: { padding: '0 0 0.6rem', border: 'none', borderBottom: '2px solid transparent', background: 'transparent', color: 'var(--muted)', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'var(--font-head)', letterSpacing: '0.02em' },
  subTabActive: { color: 'var(--cyan)', borderBottomColor: 'var(--cyan)' },
  label: { display: 'block', fontSize: '0.72rem', fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.35rem' },
  input: { display: 'block', width: '100%', padding: '0.65rem 0.9rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--white)', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: '0.85rem' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' },
  btn: { width: '100%', padding: '0.85rem', background: 'var(--cyan)', border: 'none', borderRadius: '8px', color: '#0a0a0a', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'var(--font-head)', letterSpacing: '0.02em', marginBottom: '0.6rem', transition: 'opacity 0.2s' },
  link: { background: 'none', border: 'none', color: 'var(--cyan)', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit', padding: 0 },
  linkRow: { display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' },
  err: { background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.25)', borderRadius: '6px', padding: '0.6rem 0.9rem', marginBottom: '0.85rem', fontSize: '0.82rem', color: '#ff8888' },
  success: { background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: '6px', padding: '0.6rem 0.9rem', marginBottom: '0.85rem', fontSize: '0.82rem', color: '#4ade80' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem', fontSize: '0.82rem', color: 'var(--muted)', cursor: 'pointer' },
  fieldset: { border: '1px solid var(--border)', borderRadius: '8px', padding: '0.85rem', marginBottom: '0.85rem' },
};

export default function EmailAuthForm({ onDiscordLogin, onLoginSuccess, onRequires2Fa }) {
  const [tab, setTab] = useState('discord'); // 'discord' | 'email'
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showResend, setShowResend] = useState(false);

  // Login
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPassword2, setRegPassword2] = useState('');
  const [showBilling, setShowBilling] = useState(false);
  const [billingStreet, setBillingStreet] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [billingCity, setBillingCity] = useState('');

  // Forgot password
  const [forgotEmail, setForgotEmail] = useState('');

  const resetMessages = () => { setError(''); setSuccess(''); setShowResend(false); };

  const switchMode = (m) => { setMode(m); resetMessages(); };

  const handleLogin = async () => {
    resetMessages();
    if (!EMAIL_RE.test(loginEmail)) return setError('Bitte gib eine gültige E-Mail-Adresse an.');
    if (!loginPassword) return setError('Bitte gib dein Passwort ein.');

    setLoading(true);
    try {
      const r = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const d = await r.json();

      if (!r.ok || !d.success) {
        if (d.code === 'EMAIL_NOT_VERIFIED') setShowResend(true);
        setError(d.error || 'Login fehlgeschlagen.');
        return;
      }

      if (d.requires_2fa) {
        onRequires2Fa();
      } else {
        onLoginSuccess(d.token);
      }
    } catch {
      setError('Netzwerkfehler. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    resetMessages();
    try {
      const r = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail }),
      });
      const d = await r.json();
      setSuccess(d.message || 'Bestätigungslink wurde erneut gesendet.');
    } catch {
      setError('Netzwerkfehler. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    resetMessages();
    if (!firstName.trim() || !lastName.trim()) return setError('Bitte gib deinen vollständigen Namen an.');
    if (!EMAIL_RE.test(regEmail)) return setError('Bitte gib eine gültige E-Mail-Adresse an.');
    if (regPassword.length < 8) return setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
    if (regPassword !== regPassword2) return setError('Die Passwörter stimmen nicht überein.');
    if (showBilling && (!billingStreet.trim() || !billingZip.trim() || !billingCity.trim())) {
      return setError('Bitte fülle alle Felder der Rechnungsadresse aus oder lasse sie leer.');
    }

    setLoading(true);
    try {
      const body = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: regEmail,
        password: regPassword,
      };
      if (showBilling) {
        body.billing_street = billingStreet.trim();
        body.billing_zip = billingZip.trim();
        body.billing_city = billingCity.trim();
      }

      const r = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();

      if (!r.ok || !d.success) {
        setError(d.error || 'Registrierung fehlgeschlagen.');
        return;
      }

      setSuccess(d.message);
      setFirstName(''); setLastName(''); setRegEmail(''); setRegPassword(''); setRegPassword2('');
      setShowBilling(false); setBillingStreet(''); setBillingZip(''); setBillingCity('');
    } catch {
      setError('Netzwerkfehler. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    resetMessages();
    if (!EMAIL_RE.test(forgotEmail)) return setError('Bitte gib eine gültige E-Mail-Adresse an.');

    setLoading(true);
    try {
      const r = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const d = await r.json();
      setSuccess(d.message);
    } catch {
      setError('Netzwerkfehler. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={s.tabs}>
        <button style={{ ...s.tab, ...(tab === 'discord' ? s.tabActive : {}) }} onClick={() => { setTab('discord'); resetMessages(); }}>
          Discord
        </button>
        <button style={{ ...s.tab, ...(tab === 'email' ? s.tabActive : {}) }} onClick={() => { setTab('email'); resetMessages(); }}>
          E-Mail
        </button>
      </div>

      {tab === 'discord' && (
        <button onClick={onDiscordLogin} className="avs-btn" style={{ width: '100%', padding: '0.95rem', fontSize: '0.9rem', justifyContent: 'center', gap: '10px' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
          </svg>
          Mit Discord anmelden
        </button>
      )}

      {tab === 'email' && (
        <>
          <div style={s.subTabs}>
            <button style={{ ...s.subTab, ...(mode === 'login' ? s.subTabActive : {}) }} onClick={() => switchMode('login')}>Anmelden</button>
            <button style={{ ...s.subTab, ...(mode === 'register' ? s.subTabActive : {}) }} onClick={() => switchMode('register')}>Registrieren</button>
          </div>

          {error && <div style={s.err}>{error}</div>}
          {success && <div style={s.success}>{success}</div>}

          {mode === 'login' && (
            <>
              <label style={s.label} htmlFor="login-email">E-Mail</label>
              <input id="login-email" style={s.input} type="email" autoComplete="email" placeholder="du@beispiel.de"
                value={loginEmail} onChange={e => setLoginEmail(e.target.value)} disabled={loading} />

              <label style={s.label} htmlFor="login-password">Passwort</label>
              <input id="login-password" style={s.input} type="password" autoComplete="current-password" placeholder="••••••••"
                value={loginPassword} onChange={e => setLoginPassword(e.target.value)} disabled={loading}
                onKeyDown={e => { if (e.key === 'Enter') handleLogin(); }} />

              <button style={{ ...s.btn, opacity: loading ? 0.6 : 1 }} onClick={handleLogin} disabled={loading}>
                {loading ? 'Wird geprüft…' : 'Anmelden'}
              </button>

              {showResend && (
                <button style={s.link} onClick={handleResend} disabled={loading}>
                  Bestätigungslink erneut senden
                </button>
              )}

              <div style={s.linkRow}>
                <button style={s.link} onClick={() => switchMode('forgot')}>Passwort vergessen?</button>
              </div>
            </>
          )}

          {mode === 'register' && (
            <>
              <div style={s.row}>
                <div>
                  <label style={s.label} htmlFor="reg-firstname">Vorname</label>
                  <input id="reg-firstname" style={s.input} type="text" autoComplete="given-name" placeholder="Max"
                    value={firstName} onChange={e => setFirstName(e.target.value)} disabled={loading} />
                </div>
                <div>
                  <label style={s.label} htmlFor="reg-lastname">Nachname</label>
                  <input id="reg-lastname" style={s.input} type="text" autoComplete="family-name" placeholder="Mustermann"
                    value={lastName} onChange={e => setLastName(e.target.value)} disabled={loading} />
                </div>
              </div>

              <label style={s.label} htmlFor="reg-email">E-Mail</label>
              <input id="reg-email" style={s.input} type="email" autoComplete="email" placeholder="du@beispiel.de"
                value={regEmail} onChange={e => setRegEmail(e.target.value)} disabled={loading} />

              <label style={s.label} htmlFor="reg-password">Passwort</label>
              <input id="reg-password" style={s.input} type="password" autoComplete="new-password" placeholder="Mind. 8 Zeichen"
                value={regPassword} onChange={e => setRegPassword(e.target.value)} disabled={loading} />

              <label style={s.label} htmlFor="reg-password2">Passwort wiederholen</label>
              <input id="reg-password2" style={s.input} type="password" autoComplete="new-password" placeholder="••••••••"
                value={regPassword2} onChange={e => setRegPassword2(e.target.value)} disabled={loading} />

              <label style={s.checkboxRow}>
                <input type="checkbox" checked={showBilling} onChange={e => setShowBilling(e.target.checked)} disabled={loading} />
                Rechnungsadresse jetzt angeben (optional)
              </label>

              {showBilling && (
                <div style={s.fieldset}>
                  <label style={s.label} htmlFor="reg-billing-street">Straße & Hausnummer</label>
                  <input id="reg-billing-street" style={s.input} type="text" autoComplete="street-address" placeholder="Musterstraße 1"
                    value={billingStreet} onChange={e => setBillingStreet(e.target.value)} disabled={loading} />

                  <div style={s.row}>
                    <div>
                      <label style={s.label} htmlFor="reg-billing-zip">PLZ</label>
                      <input id="reg-billing-zip" style={{ ...s.input, marginBottom: 0 }} type="text" autoComplete="postal-code" placeholder="76437"
                        value={billingZip} onChange={e => setBillingZip(e.target.value)} disabled={loading} />
                    </div>
                    <div>
                      <label style={s.label} htmlFor="reg-billing-city">Stadt</label>
                      <input id="reg-billing-city" style={{ ...s.input, marginBottom: 0 }} type="text" autoComplete="address-level2" placeholder="Rastatt"
                        value={billingCity} onChange={e => setBillingCity(e.target.value)} disabled={loading} />
                    </div>
                  </div>
                </div>
              )}

              <button style={{ ...s.btn, opacity: loading ? 0.6 : 1 }} onClick={handleRegister} disabled={loading}>
                {loading ? 'Wird registriert…' : 'Konto erstellen'}
              </button>
            </>
          )}

          {mode === 'forgot' && (
            <>
              <label style={s.label} htmlFor="forgot-email">E-Mail</label>
              <input id="forgot-email" style={s.input} type="email" autoComplete="email" placeholder="du@beispiel.de"
                value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} disabled={loading} />

              <button style={{ ...s.btn, opacity: loading ? 0.6 : 1 }} onClick={handleForgot} disabled={loading}>
                {loading ? 'Wird gesendet…' : 'Link zum Zurücksetzen senden'}
              </button>

              <div style={s.linkRow}>
                <button style={s.link} onClick={() => switchMode('login')}>Zurück zur Anmeldung</button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
